<!-- =========================================================================================
  File Name: TodoListFormSidebar.vue
  Description: Add New Data - Sidebar component
========================================================================================== -->
<i18n src="@/locales/modules/TodoListTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		destroy-on-close
		:before-close="
			setDialogHide =>
				handleCloseDialog(() => {
					initValues();
					setDialogHide();
				}, isFormDirty)
		"
		class="custom-dialog todo-list-form-sidebar"
		:title="formTitle"
		:visible.sync="isSidebarActiveLocal"
	>
		<perfect-scrollbar ref="vueScrollBar" class="scroll-area--data-list" :settings="settings">
			<div class="pb-0">
				<!-- General Info -->
				<el-collapse v-model="activeGeneral" class="cbs-collapse mt-0">
					<el-collapse-item name="GeneralInfo">
						<template slot="title">
							<div>
								<h6 class="uppercase font-bold ml-6">{{ $t("GeneralInfo") }}</h6>
							</div>
						</template>
						<div class="p-6 pb-0">
							<!-- NAME -->
							<label for class="vs-input--label">
								{{ $t("Name") }}
								<span class="text-danger">*</span>:
							</label>
							<vs-input :data-vv-as="$t('Name')" :placeholder="`${$t('Actions.Input')} ${$t('Name')}`" v-model="dataName" class="w-full" name="name" v-validate="'required'" />
							<span class="text-danger text-sm" v-show="errors.has('name')">{{ errors.first("name") }}</span>

							<!-- ASSIGNEES -->
							<div class="mt-5">
								<label for class="vs-input--label"> {{ $t("Employee") }}<span class="text-danger">*</span>: </label>
								<cbs-relations-humanroles
									:data-vv-as="$t('Employee')"
									:placeholder="`${$t('Actions.Choose')} ${$t('Employee').toLowerCase()}`"
									name="assignees"
									v-model="assignees"
									:type="1"
									:for="'people'"
									:modelId="data.parent && data.parent.id"
									:modelType="$constant.RELATION_MODELS.TODO"
									v-validate="'required'"
								/>
								<span class="text-danger text-sm" v-show="errors.has('assignees')">{{ errors.first("assignees") }}</span>
							</div>

							<!-- project -->
							<div class="mt-5">
								<label for class="vs-input--label"> {{ $t("Project") }}: </label>
								<v-select
									:clearable="false"
									class="border-none"
									:placeholder="`${$t('Actions.Choose')} ${$t('Project').toLowerCase()}`"
									:data-vv-as="$t('Project')"
									v-model="project"
									:options="projects"
									:reduce="unit => unit.id"
									label="name"
								>
								</v-select>
								<!-- <span class="text-danger text-sm" v-show="errors.has('assignees')">{{ errors.first("assignees") }}</span> -->
							</div>

							<!-- PRIORITY -->
							<div class="my-5">
								<label for class="vs-input--label">{{ $t("PriorityLevel") }} <span class="text-danger">*</span>:</label>
								<input v-model="priority" name="priority" type="hidden" v-validate="'required'" :data-vv-as="$t('PriorityLevel')" />
								<span class="text-danger text-sm block" v-show="errors.has('priority')">{{ errors.first("priority") }}</span>
								<div class="mt-2 flex justify-between">
									<vs-radio vs-name="priority" v-model="priority" :vs-value="$constant.TODO_ITEM_PRIORITY.CRITICAL">
										<div class="flex items-center">{{ $t("Critical") }} <i class="nucleo-priority-S4 nucleo text-4xl text-gray-3 pl-2"></i></div>
									</vs-radio>

									<vs-radio vs-name="priority" v-model="priority" :vs-value="$constant.TODO_ITEM_PRIORITY.HIGH">
										<div class="flex items-center">{{ $t("High") }} <i class="nucleo-priority-S3 nucleo text-4xl text-gray-3 pl-2"></i></div>
									</vs-radio>

									<vs-radio vs-name="priority" v-model="priority" :vs-value="$constant.TODO_ITEM_PRIORITY.MEDIUM">
										<div class="flex items-center">{{ $t("Medium") }}<i class="nucleo-priority-S2 nucleo text-4xl text-gray-3 pl-2"></i></div>
									</vs-radio>
									<vs-radio vs-name="priority" v-model="priority" :vs-value="$constant.TODO_ITEM_PRIORITY.LOW">
										<div class="flex items-center">{{ $t("Low") }} <i class="nucleo-priority-S1 nucleo text-4xl text-gray-3 pl-2"></i></div>
									</vs-radio>
								</div>
							</div>

							<!-- DESCRIPTION -->
							<div class="description my-5">
								<label for class="vs-input--label">{{ $t("Description") }}:</label>
								<cbs-editor class="normal-placeholder" :placeholder="`${$t('Placeholder.InputDescription')}`" v-model="description" />
							</div>
						</div>
					</el-collapse-item>
				</el-collapse>

				<!-- Time Info -->
				<el-collapse v-model="activeGeneral" class="cbs-collapse mt-0 todo-list-form-sidebar__time-info">
					<el-collapse-item name="TimeInfo">
						<template slot="title">
							<div>
								<h6 class="uppercase font-bold ml-6">{{ $t("TimeInfo") }}</h6>
							</div>
						</template>
						<div class="mt-5 px-6">
							<!-- START DATE -->
							<div class="my-5" v-if="type === $constant.TODO_TYPE.NORMAL">
								<label for class="vs-input--label">{{ $t("StartDate") }}:</label>

								<vx-input-group>
									<template slot="append">
										<div class="append-text text-black">
											<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
										</div>
									</template>

									<cbs-date-picker
										clearable
										class="w-full"
										type="datetime"
										name="start-date"
										prefix-icon="''"
										ref="dateTimePickerRef"
										format="DD-MM-yyyy HH:mm"
										value-format="yyyy-MM-dd HH:mm:ss"
										:data-vv-as="$t('StartDate')"
										:placeholder="`${$t('Placeholder.InputStartDay')}`"
										v-model="start_date"
									/>
								</vx-input-group>
								<span class="text-danger text-sm break-normal" v-show="errors.has('start-date')">{{ errors.first("start-date") }}</span>
							</div>

							<!-- Due time -->
							<div class="due-time-container">
								<div v-if="type !== $constant.TODO_TYPE.SCHEDULE" class="flex justify-between mb-4">
									<vs-radio v-model="due_mode" :vs-value="0">{{ $t("DueTime") }}</vs-radio>
									<vs-radio v-model="due_mode" v-if="!schedule_enable" :vs-value="1">{{ $t("DueTimeExactly") }}</vs-radio>
									<div></div>
								</div>
								<div v-if="due_mode === 0">
									<div class="due-time-content">
										<div class="flex items-stretch">
											<v-select
												:clearable="false"
												class="border-none w-1/3"
												:placeholder="`${$t('Actions.Choose')} ${$t('DueTime').toLowerCase()}`"
												:data-vv-as="$t('DueTime')"
												v-model="due_unit"
												:options="dueUnits"
												:reduce="unit => unit.value"
											>
											</v-select>
											<cbs-input
												v-model.number="due_value"
												class="w-2/3"
												name="due_value"
												dataType="int"
												v-validate="{ regex: '^[1-9]\\d*$' , required: 1}"
												:data-vv-as="$t('DueTime')"
												:placeholder="`${$t('Placeholder.InputDueValue')}`"
											/>
										</div>
									</div>
									<span class="text-danger text-sm break-normal" v-show="errors.has('due_value')">{{ errors.first("due_value") }}</span>
								</div>
								<div v-else>
									<vx-input-group>
										<template slot="append">
											<div class="append-text text-black">
												<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
											</div>
										</template>
										<cbs-date-picker
											clearable
											class="w-full"
											type="datetime"
											name="deadline"
											prefix-icon="''"
											ref="dateTimePickerRef"
											format="DD-MM-yyyy HH:mm"
											value-format="yyyy-MM-dd HH:mm:ss"
											:data-vv-as="$t('DueTimeExactly')"
											:placeholder="`${$t('Actions.Choose')} ${$t('DueTimeExactly').toLowerCase()}`"
											v-model="deadline"
											v-validate="'required'"
										/>
									</vx-input-group>
									<span class="text-danger text-sm break-normal" v-show="errors.has('deadline')">{{ errors.first("deadline") }}</span>
								</div>
							</div>
						</div>
						<!-- Time repeater -->
						<div class="time-repeater mt-5 px-6" v-if="!dataId || (dataId && type === $constant.TODO_TYPE.SCHEDULE)">
							<vs-checkbox v-model="schedule_enable">Lặp lại công việc</vs-checkbox>
							<time-repeater v-if="schedule_enable" v-model="schedule_repeat" :isEnableRepeater="$constant.TODO_FILTER_FLAG['schedule-by-me'] === todoFilterFlagValue" class="mt-5" />
						</div>
					</el-collapse-item>
				</el-collapse>
				<!-- Report Forms Config -->
				<el-collapse class="cbs-collapse report-forms-config">
					<el-collapse-item name="1">
						<template slot="title">
							<h6 class="uppercase font-bold ml-6">{{ $t("AddingInfo") }}</h6>
						</template>

						<div class="mt-5 px-6">
							<!-- Relations -->
							<div class="my-5">
								<label for class="vs-input--label"> {{ categoryId === 1 ? $t("Device") : $t("Relations") }}: </label>
								<cbs-relations-humanroles
									:data-vv-as="categoryId === 1 ? $t('Device') : $t('Relations')"
									:placeholder="`${$t('Actions.Choose')} ${$t('Relations').toLowerCase()}`"
									name="relations"
									v-model="relations"
									:type="1"
									:for="'task'"
								/>
								<span class="text-danger text-sm" v-show="errors.has('relations')">{{ errors.first("relations") }}</span>

								<div class="mt-3" v-html="$t('RelationsNote')" />
							</div>

							<label>{{ $t("ReportForm") }}:</label>
							<v-select
								multiple
								v-model="forms"
								label="display_name"
								class="select-multiple"
								:filterable="false"
								:options="reportForms"
								:placeholder="`${$t('Actions.Choose')} ${$t('ReportForm').toLowerCase()}`"
								@search="fetchReportForms"
							>
								<span slot="no-options">{{ $t(reportFormsSearchText && !isSearchingReportFormsSelect ? "Notify.NoMatchingOptions" : "InputReportNameNotify") }}</span>
							</v-select>
							<div class="mt-5">
								<vs-checkbox class="ml-0" v-model="isChildrenSequence">{{ $t("ChildrenSequence") }}</vs-checkbox>
							</div>
							<!-- Attach file -->
							<div class="mt-5 mb-3">
								<label for class="vs-input--label">{{ $t("Attachments") }}:</label>
								<cbs-upload-ref v-model="attachs" />
							</div>
						</div>
					</el-collapse-item>
				</el-collapse>
			</div>
		</perfect-scrollbar>

		<div class="flex justify-center flex-wrap items-center" slot="footer">
			<vs-button :type="dataId ? 'filled' : 'border'" class="w-full sm:w-1/3 mb-3 sm:mb-0 sm:mr-6 font-bold" @click.stop="submitData(!!dataId)">
				{{ dataId ? $t("Actions.Save") : $t("Actions.SaveAndRecreate") }}
			</vs-button>
			<vs-button v-show="!dataId" class="w-full sm:w-1/3 font-bold" @click.stop="submitData(true)">{{ $t("Actions.Save") }}</vs-button>
		</div>
	</el-dialog>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import vSelect from "vue-select";
import requestV2GQL from "@/http/requests/request/request_v2_gql.js";

import CbsEditor from "@/components/cbs-editor/CbsEditor";
import TimeRepeater from "@/components/time-repeater/TimeRepeater";
import CbsUploadRef from "@/components/cbs-upload-ref/CbsUploadRef";
import AddWatchersRef from "@/components/add-watchers-ref/AddWatchersRef";

const initWatcher = {
	content: "",
	relations: null,
	remind_unit: "day",
	remind_value: null,
	can_edittable: true
};

const defaultTimeRepeater = {
	freq: "daily",
	byhour: [],
	by_fixed_times: [],
	byday: [],
	bymonth: null,
	dtstart: null,
	interval: null,
	bysetpos: null,
	bymonthday: null
};

// For custom error message
import { Validator } from "vee-validate";
const dict = {
	custom: {
		due_value: {
			regex: "Thời gian hoàn thành công việc phải là số nguyên lớn hơn hoặc bằng 1"
		}
	}
};
Validator.localize("vi", dict);

export default {
	provide() {
		return { parentValidator: this.$validator };
	},
	props: {
		isSidebarActive: {
			type: Boolean,
			required: true
		},
		data: {
			type: Object,
			default: () => {}
		},
		categoryId: {
			type: Number,
			default: 0
		},
		todoFilterFlagValue: { type: Number },
		parent: {
			type: Object
		}
	},
	components: {
		vSelect,
		CbsEditor,
		TimeRepeater,
		CbsUploadRef,
		AddWatchersRef
	},
	watch: {
		isSidebarActive(val) {
			if (!val) return;
			if (Object.entries(this.data).length === 0) {
				this.initValues();
			} else {
				const { id, name, description, assignees, priority, forms = [], watchers = [], attachs, type, relations, project_id } = this.data;
				this.dataId = id;
				this.description = description || "";
				this.project = project_id;

				if (assignees) {
					this.assignees = assignees.map(assignee => {
						return { id: assignee.id, type: assignee.type, name: assignee.name };
					});
				} else {
					this.assignees = [];
				}

				if (relations) {
					this.relations = relations.map(relation => {
						return { id: relation.id, type: relation.type, name: relation.display };
					});
				} else {
					this.relations = [];
				}

				this.dataName = name || "";
				this.priority = priority || this.priority;
				this.start_date =
					this.data.readableProperties && this.data.readableProperties.start_date ? moment(this.data.readableProperties.start_date).format("YYYY-MM-DD HH:mm:ss") : this.start_date;
				this.checklist = this.data.todoitems ? this.data.todoitems.data : [];
				this.defaultChecklist = this.data.todoitems ? this.data.todoitems.data : [];
				this.schedule_repeat = this.data.readableProperties ? { ...this.schedule_repeat, ...this.data.readableProperties.schedule_repeat } : this.schedule_repeat;
				this.schedule_enable = this.data.readableProperties ? this.data.readableProperties.schedule_enable : this.schedule_enable;

				if (this.schedule_repeat && type === this.$constant.TODO_TYPE.SCHEDULE) {
					if (this.schedule_repeat.freq === "weekly") {
						this.schedule_repeat.byday = this.schedule_repeat.byday != "" ? _.split(this.schedule_repeat.byday, ",") : [];
					}
					this.schedule_repeat.byhour = this.schedule_repeat.byhour != "" ? _.split(this.schedule_repeat.byhour, ",").map(x => +x) : "";
					this.schedule_repeat.dtstart = this.schedule_repeat.dtstart ? moment(this.schedule_repeat.dtstart).format("YYYY-MM-DD HH:mm:ss") : this.schedule_repeat.dtstart;
					this.schedule_repeat.bysetpos = this.schedule_repeat.bysetpos !== "" ? JSON.parse(this.schedule_repeat.bysetpos) : null;
					this.schedule_repeat.bymonth = this.schedule_repeat.bymonth !== "" ? JSON.parse(this.schedule_repeat.bymonth) : null;
					this.schedule_repeat.bymonthday = this.schedule_repeat.bymonthday !== "" ? JSON.parse(this.schedule_repeat.bymonthday) : null;
				} else {
					this.schedule_repeat = defaultTimeRepeater;
				}
				this.watchers = watchers.length > 0 ? this.renderWatcherFromServer(watchers) : [_.cloneDeep(initWatcher)];
				this.due_value = this.data.readableProperties ? this.data.readableProperties.due_value : this.due_value;
				this.due_unit = this.data.readableProperties ? this.data.readableProperties.due_unit : this.due_unit;
				this.attachs = attachs;
				this.type = type;
				this.forms = forms;
				this.deadline = this.data.readableProperties && this.data.readableProperties.deadline ? moment(this.data.readableProperties.deadline).format("YYYY-MM-DD HH:mm:ss") : this.deadline;
				this.due_mode = this.data.readableProperties && this.data.readableProperties.due_mode ? this.data.readableProperties.due_mode : 0;
				this.isChildrenSequence = this.data.readableProperties ? this.data.readableProperties.is_children_sequence : false;
				this.setInitFormData();
			}
		},
		data: function(newVal, oldVal) {
			if (_.isEmpty(newVal) || newVal.id !== oldVal.id) {
				this.$nextTick(() => {
					if (this.$refs.vueScrollBar) {
						this.$refs.vueScrollBar.$el.scrollTop = 0;
					}
				});
			}
		},
		computedFormData: {
			handler: function(newVal) {
				const _cloneNewVal = _.cloneDeep(newVal);
				if (!_cloneNewVal.schedule_enable) {
					_cloneNewVal.schedule_repeat = this.initFormData.schedule_repeat;
				}
				this.isFormDirty = !_.isEmpty(this.initFormData) && JSON.stringify(_cloneNewVal) !== JSON.stringify(this.initFormData) && _cloneNewVal.dataId === this.initFormData.dataId;
			},
			deep: true
		},
		schedule_enable() {
			if (this.schedule_enable) {
				this.due_mode = 0;
			}
		}
	},
	data() {
		return {
			forms: [],
			dataId: null,
			dataName: "",
			priority: 1,
			description: "",
			assignees: null,
			deviceId: null,
			start_date: "",
			checklist: [],
			watchers: [],
			schedule_repeat: defaultTimeRepeater,
			due_value: null,
			attachs: [],
			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			},
			initFormData: {},
			isFormDirty: false,
			schedule_enable: false,
			type: this.$constant.TODO_TYPE.NORMAL,
			due_unit: "day",
			dueUnits: [
				{ label: this.$t("TypeRepeater.Days"), value: "day" },
				{ label: this.$t("TypeRepeater.Hour"), value: "hour" }
			],
			relations: null,
			defaultChecklist: [],
			reportForms: [],
			reportFormsSearchText: null,
			isSearchingReportFormsSelect: false,
			due_mode: 1,
			deadline: null,
			isChildrenSequence: false,
			activeGeneral: ["GeneralInfo", "TimeInfo"],
			projects: [],
			project: null
		};
	},
	computed: {
		isSidebarActiveLocal: {
			get() {
				return this.isSidebarActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeSidebar");
				}
			}
		},
		employeeChoices() {
			return this.$store.state.todoListManagement.employees;
		},
		deviceChoices() {
			return this.$store.state.todoListManagement.devices.map(device => ({
				value: device.id,
				text: device.name
			}));
		},
		computedFormData() {
			return {
				dataId: this.dataId,
				dataName: this.dataName,
				priority: this.priority,
				description: this.description,
				assignees: this.assignees,
				deviceId: this.deviceId,
				start_date: this.start_date,
				checklist: this.checklist,
				schedule_repeat: this.schedule_repeat,
				due_value: this.due_value,
				due_unit: this.due_unit,
				watchers: this.watchers,
				type: this.type,
				relations: this.relations,
				attachs: this.attachs,
				forms: this.forms,
				schedule_enable: this.schedule_enable,
				deadline: this.deadline,
				due_mode: this.due_mode,
				isChildrenSequence: this.isChildrenSequence
			};
		},
		formTitle() {
			if (this.parent) {
				return `${this.$t("AddDetail")} - ${this.parent.name}`;
			} else {
				return this.$t(!this.dataId || this.data.isCancel ? "AddNewTodo" : "UpdateTodo") + " " + (this.type == this.$constant.TODO_TYPE.SCHEDULE ? this.$t("Periodic") : "");
			}
		}
	},
	mounted() {
		this.getProjects();
	},
	methods: {
		async getProjects() {
			try {
				const dataTodo = await this.$store.dispatch("moduleProject/fetchProjectsManagement", {});
				if (dataTodo && dataTodo.projects) {
					this.projects = dataTodo.projects;
				}
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			}
		},
		initValues() {
			if (this.data.id) return;
			this.dataId = null;
			this.dataName = "";
			this.description = "";
			this.priority = 1;
			this.deviceId = null;
			this.assignees = null;
			this.start_date = "";
			this.checklist = [];
			this.schedule_repeat = _.cloneDeep(defaultTimeRepeater);
			this.due_value = null;
			this.due_unit = "hour";
			this.watchers = [_.cloneDeep(initWatcher)];
			this.attachs = [];
			this.schedule_enable = false;
			this.type = 0;
			this.$validator.reset();
			this.relations = null;
			this.defaultChecklist = [];
			this.forms = [];
			this.isFormDirty = false;
			this.deadline = null;
			this.due_mode = 1;
			this.isChildrenSequence = false;
			this.setInitFormData();
		},
		setInitFormData() {
			this.$nextTick(() => {
				this.initFormData = {
					dataId: this.dataId,
					dataName: this.dataName,
					priority: this.priority,
					description: _.cloneDeep(this.description),
					assignees: this.assignees,
					deviceId: this.deviceId,
					start_date: this.start_date,
					checklist: this.checklist,
					schedule_repeat: _.cloneDeep(this.schedule_repeat),
					due_value: this.due_value,
					due_unit: this.due_unit,
					watchers: _.cloneDeep(this.watchers),
					type: this.type,
					relations: this.relations,
					attachs: this.attachs,
					forms: _.cloneDeep(this.forms),
					schedule_enable: this.schedule_enable,
					deadline: this.deadline,
					due_mode: this.due_mode,
					isChildrenSequence: this.isChildrenSequence
				};
			});
		},
		handleCreateOrUpdateSuccess(isCloseForm) {
			if (isCloseForm) {
				this.$emit("closeSidebar");
			}
			this.initValues();
			this.isFormDirty = false;
			this.$helper.notifySuccess({
				context: this,
				options: {
					text: this.$t(`${this.dataId && !this.data.isCancel ? "UpdateTodoSuccessful" : "CreateTodoSuccessful"}`)
				}
			});
			if (!this.dataId || this.data.isCancel) {
				this.$emit("refetchDataList");
			}
		},
		async handleCreateOrUpdateTodo(dataObj, isCloseForm) {
			try {
				this.$vs.loading();
				let response;
				if (this.dataId && !this.data.isCancel) {
					response = await this.$store.dispatch("todoListManagement/updateTodoItem", {
						...dataObj,
						id: this.dataId
					});
				} else {
					response = await this.$store.dispatch("todoListManagement/addTodoItem", dataObj);
				}

				if (response && response.id && response.type !== this.$constant.TODO_TYPE.SCHEDULE) {
					this.$emit("onCreateOrUpdateSuccess", response.id);
				}
				this.handleCreateOrUpdateSuccess(isCloseForm);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$vs.loading.close();
			}
		},
		submitData(isCloseForm = false) {
			this.$validator.validateAll().then(result => {
				if (result) {
					const assignees =
						this.assignees &&
						this.assignees.map(assignee => {
							return { id: assignee.id, type: assignee.type };
						});
					let relations = [];
					if (this.relations && _.isArray(this.relations)) {
						relations =
							this.relations &&
							this.relations.map(relation => {
								return { id: relation.id, type: relation.type };
							});
					} else if (this.relations) {
						relations = [{ type: this.relations.type, id: this.relations.id }];
					}
					try {
						let schedule_repeat = _.cloneDeep(this.schedule_repeat);
						if (schedule_repeat.freq && schedule_repeat.freq != "" && schedule_repeat.interval) {
							schedule_repeat.byhour = _.toString(this.schedule_repeat.byhour);
							schedule_repeat.byday = _.toString(this.schedule_repeat.byday);
							schedule_repeat.bymonthday = _.toString(this.schedule_repeat.bymonthday);
							schedule_repeat.interval = Number(schedule_repeat.interval);
							schedule_repeat.dtstart = this.schedule_repeat.dtstart ? moment(this.schedule_repeat.dtstart).toISOString() : this.schedule_repeat.dtstart;
						} else {
							schedule_repeat = null;
						}

						this.type = schedule_repeat && schedule_repeat.freq && schedule_repeat.freq !== "" ? this.$constant.TODO_TYPE.SCHEDULE : this.$constant.TODO_TYPE.NORMAL;
						const attachs = this.attachs ? this.attachs.map(file => Number(file.id)) : [];
						const dataObj = {
							name: this.dataName,
							relations: relations || [],
							assignees: assignees ? assignees : [],

							priority: this.priority ? this.priority : 1,
							description: this.description || "",
							check_list: this.checklist,
							watchers: this.watchers
								.filter(watcher => watcher.relations && watcher.relations.length)
								.map(watcher => {
									delete watcher.hashtag_content;
									return {
										...watcher,
										relations: watcher.relations && watcher.relations.length && watcher.relations.map(relation => ({ id: relation.id, type: relation.type }))
									};
								}),
							start_date: this.start_date ? moment(this.start_date).toISOString() : null,
							due_value: this.due_value,
							due_unit: this.due_unit,
							schedule_enable: this.schedule_enable,
							attachs: attachs,
							schedule_repeat: schedule_repeat,
							type: this.type,
							category_id: this.categoryId,
							forms: this.forms.map(form => form.id),
							deadline: this.deadline ? moment(this.deadline).toISOString() : null,
							due_mode: this.due_mode,
							parent_id: this.parent ? this.parent.id : null,
							project_id: this.project, // ? this.project.id : null,
							is_children_sequence: this.isChildrenSequence
						};
						this.handleCreateOrUpdateTodo(dataObj, isCloseForm);
					} catch (error) {
						console.error(error);
					}
				}
			});
		},
		async fetchReportForms(searchText, loading) {
			clearTimeout(this.timer);
			this.timer = setTimeout(async () => {
				try {
					loading(true);
					this.isSearchingReportFormsSelect = true;
					this.reportFormsSearchText = searchText || "";
					const response = await requestV2GQL.getRelationByModel({
						page: 1,
						limit: 100,
						name: this.reportFormsSearchText,
						model: this.$constant.RELATION_MODELS.REPORT_FORM
					});
					this.reportForms = response.map(option => ({
						...option,
						display_name: option.content
					}));
				} catch (error) {
					this.$helper.notifyError({ context: this, error });
				} finally {
					loading(false);
					this.isSearchingReportFormsSelect = false;
				}
			}, 300);
		},
		triggerClickDatetimePicker() {
			this.$refs.dateTimePickerRef.$children[0].pickerVisible = true;
		},
		onNewChecklist(data) {
			this.checklist = data;
		},

		renderWatcherFromServer(watchers) {
			return watchers.map(watch => {
				delete watch.__typename;
				delete watch.reminded_at;
				watch.content = watch.hashtag_content || "";
				return watch;
			});
		}
	}
};
</script>
<style lang="scss" scoped>
.due-time-container {
	::v-deep .due-time-content {
		border: 1px solid rgba(0, 0, 0, 0.2);
		border-radius: 5px;

		.vs__dropdown-toggle,
		.vs-input--input.normal,
		.vs-con-input input {
			border: none !important;
		}

		.vs__dropdown-toggle {
			border-right: 1px solid rgba(0, 0, 0, 0.2) !important;
			border-radius: 0;
		}
	}
}

.todo-list-form-sidebar__time-info,
.report-forms-config {
	::v-deep .el-collapse-item__wrap {
		overflow: visible;
	}
}
</style>
